import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography, CircularProgress } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider';
import { Button, Input, SearchableSelect } from '../../../components/ui';
import { templateService } from '../../whatsapp-templates/services/templateService';
import { mapBotToSelectOption } from '../../whatsapp-templates/utils/normalizeTemplate';
import { normalizeWhatsappTemplateOptions } from '../utils/templateOptions';
import { parseTemplateListResponse } from '../../whatsapp-templates/adapters/templateListAdapter';
import AnalyticsPageHeader from '../components/AnalyticsPageHeader';
import { ROUTES } from '../../../constants/routes';
import { userInformationAtom } from '../../../Services/_states';
import useAnalyticsSx from '../hooks/useAnalyticsSx';
import useAnalyticsSuperAdmin from '../hooks/useAnalyticsSuperAdmin';
import useAnalyticsPermissions from '../hooks/useAnalyticsPermissions';
import { analyticsService } from '../services/analyticsService';
import { normalizeQueryResult } from '../adapters/normalizeQueryResult';
import AnalyticsChartRenderer from '../components/charts/AnalyticsChartRenderer';
import ChartTypeToggle from '../components/viewer/ChartTypeToggle';
import FilterChips from '../components/viewer/FilterChips';
import OrganisationPicker from '../components/home/OrganisationPicker';
import { ANALYTICS_COPY } from '../constants/analyticsCopy';
import { getOrganisationId } from '../../intervene/utils/organisationSelectUtils';
import { getAnalyticsOrganisationId } from '../utils/analyticsOrgScope';
import { resolveVizFromToggle, getAvailableVisualizations } from '../utils/chartCompatibility';
import { exportReportToExcel } from '../utils/exportReportToExcel';
import { querySpecToChartType } from '../utils/chartTypeMapping';
import { resolveChartAxisLabels } from '../utils/chartAxisLabels';
import { formatFilterSummary } from '../utils/reportFilters';
import useInterveneOrgAgents from '../hooks/useInterveneOrgAgents';
import { readInterveneAudienceFilters } from '../utils/interveneFilters';
import { resolveFocusedUserDisplayName } from '../utils/intervenePreviewLabels';
import { buildViewerExportSpec } from '../utils/viewerExportSpec';
import {
  CUSTOM_DATE_PRESET,
  DATE_PRESET_IDS,
  REPORT_DEFAULT_PRESET,
  applyDateOverride,
  datePresetLabels,
  savedRangeLabel,
} from '../utils/datePresets';
import { kolkataToday, addDays } from '../utils/previousPeriod';
import { mergeInterveneAudienceFilters } from '../utils/interveneFilters';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';


const DATE_LABELS = datePresetLabels(ANALYTICS_COPY);
const VIEWER_DATE_OPTIONS = [
  { id: 'all_time', label: DATE_LABELS.all_time },
  { id: 'today', label: DATE_LABELS.today },
  { id: 'yesterday', label: DATE_LABELS.yesterday },
  { id: 'last_7_days', label: DATE_LABELS.last_7_days },
  { id: 'last_30_days', label: DATE_LABELS.last_30_days },
  { id: 'last_90_days', label: DATE_LABELS.last_90_days },
  { id: 'this_month', label: DATE_LABELS.this_month },
  { id: 'this_week', label: DATE_LABELS.this_week },
  { id: CUSTOM_DATE_PRESET, label: DATE_LABELS.custom },
];

function ReportViewerContent() {
  const { reportId } = useParams();
  const location = useLocation();
  const locationState = location.state || {};
  const sx = useAnalyticsSx();
  const navigate = useNavigate();
  const permissions = useAnalyticsPermissions();
  const isSuperAdmin = useAnalyticsSuperAdmin();
  const userInfo = useRecoilValue(userInformationAtom);
  const [organisation, setOrganisation] = useState(null);
  const [report, setReport] = useState(() => locationState.report || null);
  const [queryResult, setQueryResult] = useState(() => {
    if (locationState.preview) {
      return normalizeQueryResult(locationState.preview);
    }
    return null;
  });
  const [selectedViz, setSelectedViz] = useState(null);
  const [isLoading, setIsLoading] = useState(() => !locationState.preview);
  const [activeBotNumber, setActiveBotNumber] = useState(undefined);
  const [activeTemplateId, setActiveTemplateId] = useState(undefined);
  const [activeDateRange, setActiveDateRange] = useState(undefined);
  const [botOptions, setBotOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [isLoadingBots, setIsLoadingBots] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const { showSnackBar } = useSnackBar();

  const orgId = useMemo(() => {
    return (
      (isSuperAdmin ? getOrganisationId(organisation) : '') ||
      getAnalyticsOrganisationId(userInfo) ||
      locationState.orgId ||
      report?.organisation_id ||
      report?.org_id ||
      report?.query_spec?.organisation_id ||
      ''
    );
  }, [isSuperAdmin, organisation, userInfo, locationState.orgId, report]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!reportId) {
        setIsLoading(false);
        return;
      }
      try {
        const reportRes = await analyticsService.getSavedReport(reportId, orgId);
        if (cancelled) return;
        if (!reportRes.ok) {
          if (!queryResult) setReport(null);
          return;
        }
        const reportData = reportRes.data;
        setReport(reportData);

        const targetOrg = orgId
          || reportData?.organisation_id
          || reportData?.org_id
          || reportData?.query_spec?.organisation_id;

        if (reportData?.preview?.result) {
          setQueryResult(normalizeQueryResult(reportData.preview));
        } else if (reportData?.result) {
          setQueryResult(normalizeQueryResult(reportData));
        }

        const queryRes = await analyticsService.runQuery(reportData?.query_spec, targetOrg);
        if (!cancelled && queryRes.ok) {
          setQueryResult(normalizeQueryResult(queryRes.data));
        }
      } catch (err) {
        console.error('Error loading report in viewer:', err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reportId, orgId]);

  const savedVizMode = queryResult?.spec?.visualization?.mode;
  const reportSpec = queryResult?.spec || report?.query_spec || {};
  const availableVizOptions = useMemo(() => {
    return getAvailableVisualizations(reportSpec, queryResult?.result);
  }, [reportSpec, queryResult?.result]);

  const defaultViz = useMemo(() => {
    if (savedVizMode && savedVizMode !== 'auto' && availableVizOptions.some((o) => o.id === savedVizMode)) {
      return savedVizMode;
    }
    const chartType = querySpecToChartType(reportSpec);
    if (chartType && availableVizOptions.some((o) => o.id === chartType)) {
      return chartType;
    }
    return availableVizOptions[0]?.id || 'table';
  }, [savedVizMode, reportSpec, availableVizOptions]);

  const currentViz = selectedViz && availableVizOptions.some((o) => o.id === selectedViz)
    ? selectedViz
    : defaultViz;



  const isWhatsAppReport = reportSpec.dataset === 'whatsapp_templates';

  const currentBotNumber = activeBotNumber !== undefined
    ? activeBotNumber
    : (reportSpec.bot_number ? String(reportSpec.bot_number) : '');

  const currentTemplateId = activeTemplateId !== undefined
    ? activeTemplateId
    : (reportSpec.template_id ? String(reportSpec.template_id) : '');

  const currentDateRange = useMemo(() => {
    if (activeDateRange !== undefined) return activeDateRange;
    const specRange = reportSpec?.date_range;
    if (specRange?.preset) return { preset: specRange.preset };
    if (specRange?.start || specRange?.end) {
      return { preset: CUSTOM_DATE_PRESET, start: specRange.start || '', end: specRange.end || '' };
    }
    return { preset: 'all_time' };
  }, [activeDateRange, reportSpec]);


  useEffect(() => {
    if (!isWhatsAppReport || !orgId) return;
    let cancelled = false;

    async function loadBots() {
      setIsLoadingBots(true);
      try {
        const res = await templateService.getBotNumbers(orgId);
        if (cancelled) return;
        const rawList = Array.isArray(res.data) ? res.data : res.data?.list ?? res.data?.results ?? [];
        setBotOptions(rawList.map(mapBotToSelectOption).filter(Boolean));
      } catch {
        setBotOptions([]);
      } finally {
        if (!cancelled) setIsLoadingBots(false);
      }
    }

    loadBots();
    return () => { cancelled = true; };
  }, [isWhatsAppReport, orgId]);

  useEffect(() => {
    if (!isWhatsAppReport || !orgId) return;
    let cancelled = false;

    async function loadTemplates() {
      setIsLoadingTemplates(true);
      try {
        let list = [];
        try {
          const res = await templateService.getTemplates({
            organisation: orgId,
            page: 0,
            limit: 200,
            archived: 'false',
            botNumber: currentBotNumber || undefined,
          });
          const parsed = parseTemplateListResponse(res);
          list = parsed?.templates || res?.data?.data || res?.data || [];
        } catch {
          // fallback to analyticsService.getTemplates
        }
        if (!list || !list.length) {
          try {
            const res2 = await analyticsService.getTemplates(orgId);
            list = res2?.data || res2 || [];
          } catch {
            // ignore
          }
        }
        if (cancelled) return;
        const normalized = normalizeWhatsappTemplateOptions(list, '');
        setTemplateOptions(normalized.filter((item) => Boolean(item.id)));
      } catch {
        // ignore
      } finally {
        if (!cancelled) setIsLoadingTemplates(false);
      }
    }

    loadTemplates();
    return () => { cancelled = true; };
  }, [isWhatsAppReport, orgId, currentBotNumber]);

  const handleViewerFilterChange = async ({
    botNumber: nextBot,
    templateId: nextTpl,
    dateRange: nextRange,
    agent: nextAgent,
  }) => {
    const updatedBot = nextBot !== undefined ? nextBot : currentBotNumber;
    const updatedTpl = nextTpl !== undefined ? nextTpl : currentTemplateId;
    const updatedRange = nextRange !== undefined ? nextRange : currentDateRange;
    const updatedAgent = nextAgent !== undefined ? nextAgent : focusedAgentUsername;

    setActiveBotNumber(updatedBot);
    setActiveTemplateId(updatedTpl);
    setActiveDateRange(updatedRange);

    const baseSpec = report?.query_spec || reportSpec;
    let updatedSpec = {
      ...baseSpec,
      bot_number: updatedBot || undefined,
      template_id: updatedTpl || undefined,
    };
    if (!updatedBot) delete updatedSpec.bot_number;
    if (!updatedTpl) delete updatedSpec.template_id;

    if (updatedRange) {
      updatedSpec = applyDateOverride(updatedSpec, updatedRange);
    }

    if (isInterveneReport && updatedAgent !== undefined) {
      updatedSpec.filters = mergeInterveneAudienceFilters(baseSpec.filters || [], {
        agent: updatedAgent,
      });
    }

    setIsLoading(true);
    try {
      const queryRes = await analyticsService.runQuery(updatedSpec, orgId);
      if (queryRes.ok) {
        setQueryResult(normalizeQueryResult(queryRes.data));
      }
    } catch {
      showSnackBar('Failed to update report filter', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const isInterveneReport = reportSpec.dataset === 'intervene';
  const { agents: interveneAgents } = useInterveneOrgAgents(isInterveneReport ? orgId : '');

  const filterChips = useMemo(() => {
    const spec = reportSpec;
    const chips = [];
    if (spec.account_id) {
      chips.push({ label: ANALYTICS_COPY.businessFunctionLabel, value: spec.account_id });
    }
    if (spec.campaign_ids?.length) {
      chips.push({
        label: ANALYTICS_COPY.businessServiceLabel,
        value: spec.campaign_ids.join(', '),
      });
    }
    const activeRange = currentDateRange || spec.date_range;
    const rangeLabel = activeRange?.start && activeRange?.end
      ? `${activeRange.start} – ${activeRange.end}`
      : savedRangeLabel(activeRange, datePresetLabels(ANALYTICS_COPY));
    if (rangeLabel) {
      chips.push({ label: ANALYTICS_COPY.builderStep2DateLabel, value: rangeLabel });
    }
    if (spec.dataset === 'whatsapp_templates') {
      if (currentBotNumber) {
        chips.push({
          label: 'Bot Number',
          value: String(currentBotNumber),
        });
      }
      const selectedTemplate =
        templateOptions.find((item) => String(item.id) === String(currentTemplateId)) ||
        queryResult?.result?.templates?.find(
          (item) => String(item.id) === String(currentTemplateId)
        );
      chips.push({
        label: ANALYTICS_COPY.whatsappTemplateLabel,
        value: currentTemplateId
          ? selectedTemplate?.label || String(currentTemplateId)
          : ANALYTICS_COPY.whatsappTemplateAll,
      });
    }
    const filters = spec.filters || [];
    const audienceFilters = isInterveneReport
      ? readInterveneAudienceFilters(filters)
      : null;
    const selectedAgent = audienceFilters?.agent
      ? interveneAgents.find((item) => item.username === audienceFilters.agent)
      : null;
    if (isInterveneReport) {
      chips.push({
        label: ANALYTICS_COPY.interveneAgentFilterLabel,
        value: selectedAgent?.display_name
          || selectedAgent?.username
          || audienceFilters.agent
          || ANALYTICS_COPY.interveneAgentFilterPlaceholder,
      });
      /* Customer phone filter commented out per user request
      chips.push({
        label: ANALYTICS_COPY.interveneCustomerFilterLabel,
        value: audienceFilters.customer || ANALYTICS_COPY.interveneCustomerFilterPlaceholder,
      });
      */
    }
    filters
      .filter((filter) => !(
        isInterveneReport
        && ['response_agent_username', 'agent_user_id', 'phone_number'].includes(filter.field)
      ))
      .forEach((filter) => {
        chips.push({
          label: ANALYTICS_COPY.filterChipLabel,
          value: formatFilterSummary(filter),
        });
      });
    return chips;
  }, [reportSpec, queryResult, isInterveneReport, interveneAgents, currentBotNumber, currentTemplateId, templateOptions]);

  const exportSpec = permissions.canExport
    ? buildViewerExportSpec(reportSpec, {
      botNumber: currentBotNumber,
      templateId: currentTemplateId,
      dateRange: currentDateRange,
    })
    : null;

  const { agent: focusedAgentUsername } = readInterveneAudienceFilters(reportSpec.filters || []);
  const focusedUserDisplayName = useMemo(
    () => resolveFocusedUserDisplayName(focusedAgentUsername, interveneAgents),
    [focusedAgentUsername, interveneAgents]
  );

  const chartLabels = useMemo(() => {
    const spec = reportSpec;
    const chartType = querySpecToChartType(spec);
    return {
      ...resolveChartAxisLabels({
        chartType,
        dimension: spec.dimension,
        metric: spec.metric || 'count',
        aggregation: spec.metric_aggregation || 'count',
        measureField: spec.metric_field,
        metrics: [{ id: 'count', label: 'Record count' }],
        timeFieldOptions: [{ id: 'created_at', label: 'Created date' }],
      }),
      focusedUserDisplayName: isInterveneReport ? focusedUserDisplayName : '',
    };
  }, [reportSpec, isInterveneReport, focusedUserDisplayName]);

  return (
    <Box sx={sx.pageRoot}>
      <AnalyticsPageHeader
        title={report?.name || 'Report'}
        sectionLabel={report?.name || 'Report'}
        action={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {permissions.canCreate && reportId ? (
              <Button
                variant="primary"
                onClick={() => navigate(
                  ROUTES.FEATURES.ANALYTICS_BUILDER_EDIT.replace(':reportId', reportId)
                )}
              >
                {ANALYTICS_COPY.editReport}
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={() => navigate(ROUTES.FEATURES.ANALYTICS_BUILDER)}
            >
              {ANALYTICS_COPY.createReport}
            </Button>
            {exportSpec ? (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    await exportReportToExcel({
                      reportName: report?.name || 'Report',
                      result: queryResult?.result,
                      querySpec: exportSpec,
                      orgId,
                      columnLabels: chartLabels.columnLabels,
                      categoryLabel: chartLabels.xAxisLabel,
                      measureLabel: chartLabels.yAxisLabel || chartLabels.seriesLabel,
                    });
                  } catch {
                    showSnackBar(ANALYTICS_COPY.exportFailed, 'error');
                  }
                }}
              >
                {ANALYTICS_COPY.exportCsv}
              </Button>
            ) : null}
          </Stack>
        )}
      />

      {isSuperAdmin ? (
        <OrganisationPicker organisation={organisation} onChange={setOrganisation} sx={sx} />
      ) : null}

      <FilterChips filters={filterChips} />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'flex-end',
          p: 1.5,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          mb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 1 240px' } }}>
          <SearchableSelect
            label={ANALYTICS_COPY.builderStep2DateLabel}
            options={VIEWER_DATE_OPTIONS}
            value={VIEWER_DATE_OPTIONS.find((o) => o.id === currentDateRange?.preset) || VIEWER_DATE_OPTIONS[0]}
            onChange={(opt) => {
              const nextPreset = opt?.id || 'all_time';
              if (nextPreset === CUSTOM_DATE_PRESET) {
                handleViewerFilterChange({
                  dateRange: { preset: CUSTOM_DATE_PRESET, start: '', end: '' },
                });
              } else if (nextPreset === 'today') {
                const today = kolkataToday();
                handleViewerFilterChange({
                  dateRange: { preset: 'today', start: today, end: today },
                });
              } else if (nextPreset === 'yesterday') {
                const yDay = addDays(kolkataToday(), -1);
                handleViewerFilterChange({
                  dateRange: { preset: 'yesterday', start: yDay, end: yDay },
                });
              } else {
                handleViewerFilterChange({ dateRange: { preset: nextPreset } });
              }
            }}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || String(option?.id || ''))}
            isOptionEqualToValue={(a, b) => String(a?.id ?? a) === String(b?.id ?? b)}
            clearable={false}
          />
        </Box>

        {currentDateRange?.preset === CUSTOM_DATE_PRESET ? (
          <>
            <Box sx={{ minWidth: 150, flex: '1 1 150px' }}>
              <Input
                label={ANALYTICS_COPY.dateRangeLabel}
                type="date"
                value={currentDateRange.start || ''}
                inputProps={{ max: currentDateRange.end || undefined }}
                onChange={(e) => {
                  const start = e.target.value;
                  const newRange = { ...currentDateRange, start };
                  if (start && newRange.end && newRange.end >= start) {
                    handleViewerFilterChange({ dateRange: newRange });
                  } else {
                    setActiveDateRange(newRange);
                  }
                }}
              />
            </Box>
            <Box sx={{ minWidth: 150, flex: '1 1 150px' }}>
              <Input
                label={ANALYTICS_COPY.dateRangeEndLabel}
                type="date"
                value={currentDateRange.end || ''}
                inputProps={{ min: currentDateRange.start || undefined }}
                onChange={(e) => {
                  const end = e.target.value;
                  const newRange = { ...currentDateRange, end };
                  if (end && newRange.start && end >= newRange.start) {
                    handleViewerFilterChange({ dateRange: newRange });
                  } else {
                    setActiveDateRange(newRange);
                  }
                }}
              />
            </Box>
          </>
        ) : null}

        {isWhatsAppReport ? (
          <>
            <Box sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 1 240px' } }}>
              <SearchableSelect
                label="Bot Number"
                placeholder="All Bot Numbers"
                options={botOptions}
                value={
                  botOptions.find(
                    (b) => String(b.id) === String(currentBotNumber) || String(b.value) === String(currentBotNumber)
                  ) || (currentBotNumber ? { id: String(currentBotNumber), label: String(currentBotNumber) } : null)
                }
                onChange={(opt) => {
                  const nextBot = opt?.id || opt?.value ? String(opt.id || opt.value) : '';
                  handleViewerFilterChange({ botNumber: nextBot, templateId: '' });
                }}
                getOptionLabel={(opt) => opt?.label || String(opt?.id || '')}
                isOptionEqualToValue={(a, b) => String(a?.id || a?.value) === String(b?.id || b?.value)}
                loading={isLoadingBots}
                clearable
              />
            </Box>
            <Box sx={{ minWidth: 220, flex: { xs: '1 1 100%', sm: '0 1 260px' } }}>
              <SearchableSelect
                label={ANALYTICS_COPY.whatsappTemplateLabel}
                placeholder={ANALYTICS_COPY.whatsappTemplateAll}
                options={templateOptions}
                value={
                  templateOptions.find(
                    (t) => String(t.id) === String(currentTemplateId) || String(t.label) === String(currentTemplateId)
                  ) || (currentTemplateId ? { id: String(currentTemplateId), label: String(currentTemplateId) } : null)
                }
                onChange={(opt) => {
                  const nextTpl = opt?.id ? String(opt.id) : '';
                  handleViewerFilterChange({ templateId: nextTpl });
                }}
                getOptionLabel={(opt) => opt?.label || String(opt?.id || '')}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                loading={isLoadingTemplates}
                clearable
              />
            </Box>
          </>
        ) : null}

        {isInterveneReport && interveneAgents.length ? (
          <Box sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 1 240px' } }}>
            <SearchableSelect
              label={ANALYTICS_COPY.interveneAgentFilterLabel}
              placeholder={ANALYTICS_COPY.interveneAgentFilterPlaceholder}
              options={interveneAgents.map((a) => ({ id: a.username, label: a.display_name || a.username }))}
              value={
                interveneAgents.find((a) => a.username === focusedAgentUsername)
                  ? { id: focusedAgentUsername, label: focusedUserDisplayName }
                  : null
              }
              onChange={(opt) => {
                const nextAgent = opt?.id ? String(opt.id) : '';
                handleViewerFilterChange({ agent: nextAgent });
              }}
              getOptionLabel={(opt) => opt?.label || String(opt?.id || '')}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              clearable
            />
          </Box>
        ) : null}
      </Box>

      <ChartTypeToggle
        value={currentViz}
        onChange={setSelectedViz}
        options={availableVizOptions}

      />

      {isLoading ? (
        <Stack alignItems="center" py={6}>
          <CircularProgress />
        </Stack>
      ) : queryResult ? (
        <AnalyticsChartRenderer
          result={queryResult.result}
          visualization={currentViz}
          chartLabels={chartLabels}
          sx={sx}
          querySpec={reportSpec}
          orgId={orgId}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          Report not found or could not load data.
        </Typography>
      )}
    </Box>
  );
}

export default function ReportViewerPage() {
  return (
    <FeatureThemeProvider feature="analytics">
      <ReportViewerContent />
    </FeatureThemeProvider>
  );
}
